import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Suspense } from "react";
import { HashRouter as Router } from "react-router-dom";
import GlobalStyles from "../GlobalStyles";
import { FullPageSpinner } from "../components/lib";
import { LOCAL_STORAGE_KEY } from "../constants/localStorage";
import { MessageProvider, MessageViewer } from "../context/message-context";
import darktheme from "../theme/darkTheme";
import lighttheme from "../theme/lightTheme";
import ScrollToTop from "../utils/ScrollToTop";
import { useLocalStorage } from "../utils/hooks";
import { AdminProvider } from "./admin-context";
import { AuthProvider } from "./auth-context";
import { ConfirmProvider } from "./confirm-context";
import { DebugProvider } from "./debug-context";
import { DeviceNameProvider } from "./devicename-context";
import { GlobalStateProvider } from "./global-state-context";
import { NotificationProvider } from "./notification-context";
import { PlatformProvider } from "./platform-context";
import { ZulassungsProvider } from "./zulassungs-context";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from "@mui/x-date-pickers";
import 'dayjs/locale/de';

function AppProviders(props) {
  const [theme, setTheme] = useLocalStorage(LOCAL_STORAGE_KEY.theme);
  const isDarkModeEnabled = useMediaQuery("(prefers-color-scheme: dark)");

  const getTheme = () => {
    if (theme !== "dark" && theme !== "light" && theme !== "system") setTheme("system");
    if (theme === "dark") return darktheme;
    if (theme === "system" && isDarkModeEnabled) return darktheme;
    if (theme === "" && isDarkModeEnabled) return darktheme;
    return lighttheme;
  };

  const cache = createCache({
    key: "css",
    nonce: "29d37e82ba",
    prepend: true,
  });

  return (
    <CacheProvider value={cache}>
      <Suspense fallback={<FullPageSpinner />}>
        <Router>
          <ScrollToTop />
          <ThemeProvider theme={getTheme()}>
            <AuthProvider>
              <DebugProvider>
                <AdminProvider>
                  <ZulassungsProvider>
                    <PlatformProvider>
                      <DeviceNameProvider>
                        <NotificationProvider>
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="de">
                            <CssBaseline />
                            <GlobalStyles />
                            <GlobalStateProvider>
                              <ConfirmProvider>
                                <MessageProvider>
                                  {props.children}
                                  <MessageViewer />
                                </MessageProvider>
                              </ConfirmProvider>
                            </GlobalStateProvider>
                          </LocalizationProvider>
                        </NotificationProvider>
                      </DeviceNameProvider>
                    </PlatformProvider>
                  </ZulassungsProvider>
                </AdminProvider>
              </DebugProvider>
            </AuthProvider>
          </ThemeProvider>
        </Router>
      </Suspense>
    </CacheProvider>
  );
}

export default AppProviders;
